/**
 * Wire format ↔ dominio Planiwise para la IA de formularios.
 * @see ModeloFormularioForm convertToOpenAIFormat / convertToLocalFormat
 */

/** Valores de answerType en JSON hacia/desde OpenAI (vocabulario wire). */
export const WIRE_ANSWER_TYPES = [
  'text',
  'number',
  'checkbox',
  'selection',
  'checkBoxList',
  'yesNo',
  'date',
  'time',
  'day',
  'month',
  'year',
  'inspectionCheck',
  'location',
  'ammount',
  'rut',
  'barcode',
  'thirdParty',
  'perfilEmpleado',
  'usuario',
  'asignadorFuncion',
  'asset',
  'articulo',
  'project',
  'files',
  'textFromImage',
  'signature',
  'meterReading',
] as const;

export type WireAnswerType = (typeof WIRE_ANSWER_TYPES)[number];

/** Tipos incidente (mismo string que tipoRespuesta en dominio). Debe alinearse con PreguntaFormularioForm tipoRespuestaIncidenteOptions. */
export const INCIDENT_ANSWER_TYPES: string[] = [
  'incident_incidentCode',
  'incident_withLostDays',
  'incident_initialClassification',
  'incident_finalClassification',
  'incident_severity',
  'incident_label',
  'incident_incidentDate',
  'incident_incidentDateTime',
  'incident_dateFrom',
  'incident_dateFromTime',
  'incident_dateTo',
  'incident_responsibleAdvisor',
  'incident_areaManager',
  'incident_incidentLocation',
  'incident_incidentDescription',
  'incident_narrativeAccount',
  'incident_incidentStatus',
  'incident_company',
  'incident_employee',
  'incident_daysAdjustment',
  'incident_entryType',
  'incident_riesgo',
  'incident_contractType',
  'incident_narrativeWhere',
  'incident_narrativeHow',
  'incident_narrativeWhen',
  'incident_contractorCompany',
  'incident_management',
  'incident_submissionDate',
  'incident_reportDate',
  'incident_horaIngresoTrabajo',
  'incident_horaSalidaTrabajo',
  'incident_fullName',
  'incident_registrationNumber',
  'incident_email',
  'incident_jobPosition',
  'incident_companyStartDate',
  'incident_contractEndDate',
  'incident_companyExperienceYears',
  'incident_companyExperienceMonths',
  'incident_companyExperienceDays',
  'incident_jobStartDate',
  'incident_workExperienceYears',
  'incident_workExperienceMonths',
  'incident_workExperienceDays',
  'incident_ageYears',
  'incident_gender',
  'incident_birthDate',
  'incident_nationality',
  'incident_injuredZoneSpecific',
  'incident_locations',
  'incident_age',
  'incident_workExperience',
  'incident_causaInmediata1',
  'incident_causaInmediata2',
  'incident_causaInmediata3',
  'incident_causaBasica1',
  'incident_causaBasica2',
  'incident_causaBasica3',
  'incident_medidasDeControl',
  'incident_medidas_control_medida',
  'incident_medidas_control_responsable',
  'incident_testigos',
  'incident_testigosImages',
  'incident_images',
  'incident_contactPhone',
  'incident_directManagerPosition',
  'incident_propertyDamage',
  'incident_workPerformed',
  'incident_atSea',
  'incident_causingAgent',
  'incident_injuryType',
  'incident_narratorName',
  'incident_narratorRole',
  'incident_causalEffect',
  'incident_causalProblem',
  'incident_causalCause1',
  'incident_causalCause2',
  'incident_causalCause3',
  'incident_investigationConclusion',
  'incident_incidentWeekday',
  'incident_accionesSubestandar',
  'incident_condicionesSubestandar',
  'incident_tipoAccidente',
];

const DOMAIN_TO_WIRE: Record<string, string> = {
  textField: 'text',
  numberField: 'number',
  checkbox: 'checkbox',
  multipleChoice: 'selection',
  multiCheckbox: 'checkBoxList',
  yesNo: 'yesNo',
  date: 'date',
  time: 'time',
  day: 'day',
  month: 'month',
  year: 'year',
  inspectionCheck: 'inspectionCheck',
  location: 'location',
  ammount: 'ammount',
  registrationNumber: 'rut',
  barcode: 'barcode',
  thirdParty: 'thirdParty',
  perfilEmpleado: 'perfilEmpleado',
  usuario: 'usuario',
  asignadorFuncion: 'asignadorFuncion',
  asset: 'asset',
  articulo: 'articulo',
  project: 'project',
  files: 'files',
  textFromImage: 'textFromImage',
  signature: 'signature',
  meterReading: 'meterReading',
};

const WIRE_TO_DOMAIN: Record<string, string> = Object.fromEntries(
  Object.entries(DOMAIN_TO_WIRE).map(([d, w]) => [w, d]),
);

const ALL_KNOWN_DOMAIN_TYPES = new Set([
  ...Object.keys(DOMAIN_TO_WIRE),
  ...INCIDENT_ANSWER_TYPES,
]);

export function getWireAnswerTypesForClasificacion(clasificacion: string): string[] {
  const base = [...WIRE_ANSWER_TYPES];
  if (clasificacion === 'incidente' || clasificacion === 'investigacionIncidente') {
    return [...base, ...INCIDENT_ANSWER_TYPES];
  }
  return base;
}

export function domainAnswerTypeToWire(tipoRespuesta: string | undefined): string {
  if (!tipoRespuesta) return 'text';
  if (INCIDENT_ANSWER_TYPES.includes(tipoRespuesta)) return tipoRespuesta;
  return DOMAIN_TO_WIRE[tipoRespuesta] ?? tipoRespuesta;
}

/**
 * Convierte answerType wire (o legacy dominio) a tipoRespuesta dominio.
 */
export function wireAnswerTypeToDomain(answerType: string | undefined): string {
  if (!answerType) return 'textField';
  if (INCIDENT_ANSWER_TYPES.includes(answerType)) return answerType;
  if (ALL_KNOWN_DOMAIN_TYPES.has(answerType)) return answerType;
  const fromWire = WIRE_TO_DOMAIN[answerType];
  if (fromWire) return fromWire;
  console.warn('[modeloFormularioAi] answerType desconocido, usando textField:', answerType);
  return 'textField';
}

/** Reconstruye answerType wire incident_* desde dominio (tras fakeResponseTypeToNormal: textField + keyPointer). */
export function domainToWireAnswerTypeForExport(
  tipoRespuesta: string | undefined,
  keyPointer: string | undefined,
  clasificacion: string,
): string {
  if (
    (clasificacion === 'incidente' || clasificacion === 'investigacionIncidente') &&
    keyPointer &&
    tipoRespuesta &&
    !tipoRespuesta.startsWith('incident_')
  ) {
    return `incident_${keyPointer}`;
  }
  if (tipoRespuesta?.startsWith('incident_')) return tipoRespuesta;
  return domainAnswerTypeToWire(tipoRespuesta);
}

export function incidentKeyPointerFromTipo(tipoIncident: string): string {
  const parts = tipoIncident.split('_');
  return parts.length >= 2 ? parts.slice(1).join('_') : '';
}

/** Claves wire en JSON de pregunta (incl. cortas). */
export const WIRE_QUESTION_KEYS = new Set([
  'name',
  'label',
  'questionIndex',
  'answerType',
  'options',
  'required',
  'allowDateBefore',
  'haveCriticality',
  'havePhotos',
  'haveFiles',
  'tieneComentario',
  'commentLabel',
  'allowOthersOption',
  'numMin',
  'numMax',
  'hasAlertNumberRange',
  'alertNumberRange',
  'invertAlertNumberRange',
  'amountMin',
  'amountMax',
  'allowMultipleAnswers',
  'multipleAnswersType',
  'onlyOneOption',
  'defaultFirstOption',
  'allowNotApplicable',
  'alternativeTree',
  'alternativeTreeDisplayLevels',
  'restrictByLocation',
  'hiddenInPdf',
  'tieneLink',
  'link',
  'tienePuntaje',
  'puntaje',
  'unidadMedida',
  'keyPointer',
]);

/** Wire → dominio para campos de pregunta (Fase A + B parcial). */
const WIRE_TO_DOMAIN_QUESTION: Record<string, string> = {
  numMin: 'numberFieldMin',
  numMax: 'numberFieldMax',
  amountMin: 'ammountMin',
  amountMax: 'ammountMax',
};

const DOMAIN_TO_WIRE_QUESTION: Record<string, string> = Object.fromEntries(
  Object.entries(WIRE_TO_DOMAIN_QUESTION).map(([w, d]) => [d, w]),
);

export function wireQuestionKeyToDomain(key: string): string {
  return WIRE_TO_DOMAIN_QUESTION[key] ?? key;
}

export function domainQuestionKeyToWire(key: string): string {
  return DOMAIN_TO_WIRE_QUESTION[key] ?? key;
}

/** Copia campos permitidos de question wire → objeto dominio (sin answerType/options). */
export function applyWireQuestionFieldsToDomain(question: Record<string, any>, target: Record<string, any>) {
  if ('required' in question) {
    target.isRequired = Boolean(question.required);
  }
  const skip = new Set(['name', 'label', 'questionIndex', 'answerType', 'options', 'required', 'systemMessage']);
  Object.keys(question).forEach((key) => {
    if (skip.has(key) || !WIRE_QUESTION_KEYS.has(key)) return;
    const domainKey = wireQuestionKeyToDomain(key);
    if (question[key] !== undefined) {
      target[domainKey] = question[key];
    }
  });
}

/** Serializa campos de pregunta dominio → wire (Fase A keys). */
const DOMAIN_KEYS_ROUNDTRIP = [
  'isRequired',
  'allowDateBefore',
  'haveCriticality',
  'havePhotos',
  'haveFiles',
  'tieneComentario',
  'commentLabel',
  'allowOthersOption',
  'numberFieldMin',
  'numberFieldMax',
  'hasAlertNumberRange',
  'alertNumberRange',
  'invertAlertNumberRange',
  'ammountMin',
  'ammountMax',
  'allowMultipleAnswers',
  'multipleAnswersType',
  'onlyOneOption',
  'defaultFirstOption',
  'allowNotApplicable',
  'alternativeTree',
  'alternativeTreeDisplayLevels',
  'restrictByLocation',
  'hiddenInPdf',
  'tieneLink',
  'link',
  'tienePuntaje',
  'puntaje',
  'unidadMedida',
  'keyPointer',
];

export function pickDomainQuestionFieldsForWire(pregunta: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  if (pregunta.isRequired !== undefined) out.required = pregunta.isRequired;
  DOMAIN_KEYS_ROUNDTRIP.forEach((dk) => {
    if (dk === 'isRequired') return;
    if (pregunta[dk] !== undefined && pregunta[dk] !== null) {
      const wk = domainQuestionKeyToWire(dk);
      out[wk] = pregunta[dk];
    }
  });
  return out;
}

export type WireOptionNode = {id?: string; label: string; children?: WireOptionNode[]};

function isFlatOptions(opts: any): opts is string[] {
  return Array.isArray(opts) && (opts.length === 0 || typeof opts[0] === 'string');
}

/** Normaliza árbol wire → dominio (normalizeAlternativeNodes style). */
export function normalizeWireOptionsTree(nodes: any, depth = 1): any[] {
  if (!Array.isArray(nodes)) return [];
  const MAX_DEPTH = 3;
  return nodes
    .map((node, index) => {
      if (typeof node === 'string') {
        const label = String(node).trim();
        if (!label) return null;
        return {id: `ai_${index}_${label}`, label, children: []};
      }
      const labelValue = `${node?.label ?? node?.value ?? ''}`.trim();
      if (!labelValue) return null;
      const normalized: any = {
        id: String(node?.id ?? `ai_${Date.now()}_${index}`),
        label: labelValue,
        children: [],
      };
      if (depth < MAX_DEPTH && Array.isArray(node?.children)) {
        normalized.children = normalizeWireOptionsTree(node.children, depth + 1);
      }
      return normalized;
    })
    .filter(Boolean);
}

export function wireOptionsToAlternativas(
  options: any,
  alternativeTree: boolean | undefined,
  mode: 'selection' | 'checkBoxList',
  createOptionLabelId: (opt: any, index: number) => {id: string; label: string},
): {alternativas?: any[]; alternativasCheckbox?: any[]} {
  if (options == null) return {};
  if (alternativeTree) {
    const tree = normalizeWireOptionsTree(options);
    return mode === 'selection' ? {alternativas: tree} : {alternativasCheckbox: tree};
  }
  if (isFlatOptions(options)) {
    const mapped = options.map(createOptionLabelId);
    return mode === 'selection' ? {alternativas: mapped} : {alternativasCheckbox: mapped};
  }
  const tree = normalizeWireOptionsTree(options);
  return mode === 'selection' ? {alternativas: tree} : {alternativasCheckbox: tree};
}

export function domainOptionsToWire(
  alternativas: any[] | undefined,
  alternativasCheckbox: any[] | undefined,
  tipoRespuesta: string,
  alternativeTree: boolean | undefined,
): any {
  const src = tipoRespuesta === 'multiCheckbox' ? alternativasCheckbox : alternativas;
  if (!Array.isArray(src) || !src.length) return undefined;
  if (alternativeTree) {
    const mapNode = (n: any): WireOptionNode => ({
      id: n.id,
      label: n.label ?? n.value ?? '',
      children: Array.isArray(n.children) ? n.children.map(mapNode) : undefined,
    });
    return src.map(mapNode);
  }
  return src.map((a) => a.label ?? a);
}
